import { catchError, firstValueFrom, of, timeout } from 'rxjs';
import { storageHelper } from '../../helpers/storage';
import { appState } from '../app/app.state';
import { authService } from '../auth/auth.service';
import {
  normalizeCompanyGuid,
  purgeTenantScopedStorage,
  readStoredCompanyGuid,
  writeStoredCompanyGuid,
} from './tenant.helper';

/**
 * Tenant guard — detects that this tab's active company changed underneath it
 * and resets the app instead of letting it keep serving another database's
 * cached data (WA-3495, and the wrong-database write it caused in WA-3581).
 *
 * See `tenant.helper.ts` for WHY a live tab can silently change tenant. The
 * short version: the idToken lives in a cookie shared with the console, so a
 * company switch there re-targets this tab at the next token refresh.
 *
 * Detection points, cheapest first:
 *   - boot, before the first render (see `main.tsx`);
 *   - a poll on a short interval — a plain token read (no `forceRefresh`)
 *     answers from cookie storage without a network call, so this is nearly
 *     free and bounds how long a foreground tab can run mis-targeted;
 *   - tab visibility, with a forced token refresh (rate-limited) — this is the
 *     actual support workflow: switch in the console tab, come back to this one.
 *
 * The only sound reaction to a switch is a full page reload: the ~40
 * `configsData.dict*` dropdown dictionaries live in a module-scoped in-memory
 * map, and several states read sessionStorage at module-init time, so there is
 * no way to reset them from here.
 */

/** Non-forced reads are local; keep the ceiling low so boot never hangs on it. */
const RESOLVE_TIMEOUT_MS = 3_000;
/** Forced reads hit Cognito. */
const FORCED_RESOLVE_TIMEOUT_MS = 10_000;
const POLL_INTERVAL_MS = 30_000;
/** A forced refresh costs a Cognito round-trip — never more than one a minute. */
const FORCED_REFRESH_MIN_INTERVAL_MS = 60_000;
/** Grace before the automatic reload, so the popup is readable first. */
const RELOAD_GRACE_MS = 5_000;
/**
 * How long the first render may wait for the boot check.
 *
 * `index.html` has no static loading state, so until React mounts the page is
 * blank — the guard must never be able to hold that longer than a blink. When
 * the deadline wins the app renders and the check keeps running in the
 * background; a switch found late still goes through the popup + reload path.
 */
const BOOT_DEADLINE_MS = 1_200;
/**
 * Two switch-triggered reloads closer than this mean something is flapping
 * rather than a real switch; fall back to a manual reload instead of spinning.
 */
const RELOAD_COOLDOWN_MS = 15_000;

export type TenantCheckResult =
  /** No company could be resolved (signed out, or the session is being renewed). */
  | 'unknown'
  /** First check of this tab — the current tenant is now recorded. */
  | 'adopted'
  | 'unchanged'
  | 'switched';

let watching = false;
let checkInFlight: Promise<TenantCheckResult> | null = null;
let lastForcedRefreshAt = 0;
let reloadTimerId: ReturnType<typeof setTimeout> | null = null;

/**
 * Resolve the company the NEXT GraphQL request would be served by.
 *
 * Deliberately built on `authService.sub.customerGUID()` — the exact value
 * `gqlClient` resolves per request — so the guard can never disagree with the
 * requests it is guarding. Every failure resolves to `''` ("unknown"): a guard
 * that cannot read the session must stay silent, never reload.
 */
async function readLiveCompanyGuid(forceRefresh: boolean): Promise<string> {
  if (forceRefresh) {
    lastForcedRefreshAt = Date.now();
    await firstValueFrom(
      authService.sub.refreshIdToken().pipe(
        timeout({ first: FORCED_RESOLVE_TIMEOUT_MS }),
        catchError(() => of('')),
      ),
    );
  }
  const guid = await firstValueFrom(
    authService.sub.customerGUID().pipe(
      timeout({ first: RESOLVE_TIMEOUT_MS }),
      catchError(() => of('')),
    ),
  );
  return normalizeCompanyGuid(guid);
}

/** Purge every tenant-scoped cache and restart the app on the current tenant. */
export function reloadIntoCurrentTenant(): void {
  if (reloadTimerId !== null) {
    clearTimeout(reloadTimerId);
    reloadTimerId = null;
  }
  // Purge as late as possible: the running app keeps writing selections to
  // sessionStorage right up to the reload, so an earlier clear would be partly
  // refilled with the previous tenant's data before the page goes away.
  purgeTenantScopedStorage();
  window.location.reload();
}

function handleSwitch(): void {
  const now = Date.now();
  const lastReloadAt = Number(storageHelper.local.getItem('tenantGuard.lastReloadAt')) || 0;
  // The marker lives in localStorage on purpose — `purgeTenantScopedStorage()`
  // wipes sessionStorage, so a sessionStorage marker could never detect a loop.
  const isFlapping = now - lastReloadAt < RELOAD_COOLDOWN_MS;

  // Tell the UI first; the popup is what explains the imminent reload, and it
  // is also the only way out when the auto-reload is suppressed below.
  appState.pub.companySwitched();

  if (isFlapping || reloadTimerId !== null) return;
  storageHelper.local.setItem('tenantGuard.lastReloadAt', now);
  // Always scheduled, never immediate: `bootstrapTenantGuard()` pre-empts this
  // timer when the switch is found before the first render.
  reloadTimerId = setTimeout(reloadIntoCurrentTenant, RELOAD_GRACE_MS);
}

/**
 * Compare the tab's recorded tenant against the live one.
 *
 * Concurrent callers (poll firing while a visibility check runs) share one
 * in-flight check so a switch is handled once, not once per trigger.
 */
export function checkTenant(options: { forceRefresh?: boolean } = {}): Promise<TenantCheckResult> {
  if (checkInFlight) {
    // Never drop a forced refresh request: wait for the current check, then run a forced one.
    if (!options.forceRefresh) return checkInFlight;
    return checkInFlight.then((result) =>
      result === 'switched' ? result : checkTenant({ forceRefresh: true }),
    );
  }

  checkInFlight = (async (): Promise<TenantCheckResult> => {
    const live = await readLiveCompanyGuid(Boolean(options.forceRefresh));
    // No readable company: signed out, or mid-refresh. Never treat this as a
    // switch — reloading a tab whose session is simply expiring would fight
    // with the login redirect.
    if (!live) return 'unknown';

    const stored = readStoredCompanyGuid();
    if (!stored) {
      writeStoredCompanyGuid(live);
      return 'adopted';
    }
    if (stored === live) return 'unchanged';

    handleSwitch();
    return 'switched';
  })().finally(() => {
    checkInFlight = null;
  });

  return checkInFlight;
}

/**
 * Boot check, awaited before the first render. Resolves whether to render.
 *
 * `false` means a reload is already under way and the caller must NOT render:
 * this page load has already imported modules that read the previous tenant's
 * sessionStorage at module-init time, so the only clean state is the one after
 * the reload.
 */
export async function bootstrapTenantGuard(): Promise<boolean> {
  // Not forced: at boot the cookie already holds whatever the console left
  // behind, and a Cognito round-trip here would delay first paint on every
  // single page load. `startTenantGuard()` does the forced confirmation
  // immediately after render instead.
  //
  // The deadline caps how long a blank page may be shown. The losing check is
  // NOT cancelled, so a switch found after it still gets handled — just through
  // the normal popup + grace path, which by then is the right one anyway.
  const check = checkTenant();
  const deadline = new Promise<TenantCheckResult>((resolve) => {
    setTimeout(() => resolve('unknown'), BOOT_DEADLINE_MS);
  });
  const result = await Promise.race([check, deadline]);

  if (result !== 'switched') return true;
  // Flap protection suppressed the reload. Render rather than leave a blank
  // page; the next poll re-detects the mismatch once the cooldown has passed.
  if (reloadTimerId === null) return true;

  // Pre-empt the scheduled grace period: nothing is rendered yet, so there is
  // no popup to read and no user work to lose.
  reloadIntoCurrentTenant();
  return false;
}

/** Install the runtime watchers. Idempotent. */
export function startTenantGuard(): void {
  if (watching) return;
  watching = true;

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState !== 'visible') return;
    const forceRefresh = Date.now() - lastForcedRefreshAt >= FORCED_REFRESH_MIN_INTERVAL_MS;
    void checkTenant({ forceRefresh });
  });

  setInterval(() => void checkTenant(), POLL_INTERVAL_MS);

  // Confirm the boot result against Cognito rather than against the cookie
  // alone: if the console switched the company without this tab ever refreshing
  // its token, the switch only becomes visible once the token is renewed. Doing
  // it here — after render, before the user can realistically act — keeps the
  // detection deterministic without delaying first paint.
  void checkTenant({ forceRefresh: true });
}
